<template>
  <div v-if="node.type === 'field'" class="max-field" :data-input="node.input">
    <label v-if="node.input === 'toggle' || node.input === 'checkbox'" class="max-field__toggle" :for="id">
      <input
        :id="id"
        type="checkbox"
        :role="node.input === 'toggle' ? 'switch' : undefined"
        :checked="node.value === true"
        :disabled="disabled"
        :aria-describedby="node.help ? id + '-help' : undefined"
        @change="commit(($event.target as HTMLInputElement).checked)"
      >
      <span :class="{ 'max-sr-only': node.hideLabel }">{{ node.label }}</span>
    </label>

    <fieldset v-else-if="isChoice" class="max-choice" :aria-describedby="node.help ? id + '-help' : undefined">
      <legend :class="node.hideLabel ? 'max-sr-only' : 'max-field__label'">{{ node.label }}</legend>
      <div
        v-if="node.input === 'chips' || node.input === 'segmented'"
        :class="node.input === 'segmented' ? 'max-segmented' : 'max-chips'"
        role="radiogroup"
        :aria-label="node.label"
      >
        <button
          v-for="(option, i) in flatOptions"
          :key="i"
          type="button"
          role="radio"
          :class="node.input === 'segmented' ? 'max-segmented__option' : 'max-chip max-chip--choice'"
          :aria-checked="same(option.value, node.value)"
          :disabled="disabled"
          :tabindex="same(option.value, node.value) || (i === 0 && !hasValue) ? 0 : -1"
          @click="commit(option.value)"
          @keydown="arrowKeys($event, i)"
        >{{ option.label }}</button>
      </div>
      <template v-else>
        <div v-for="(group, g) in optionGroups" :key="g" class="max-choice__group">
          <p v-if="group.label" class="max-eyebrow">{{ group.label }}</p>
          <label v-for="(option, i) in group.options" :key="i" class="max-field__toggle max-choice__option">
            <input
              v-if="node.input === 'radio'"
              type="radio"
              :name="id"
              :checked="same(option.value, node.value)"
              :disabled="disabled"
              @change="commit(option.value)"
            >
            <input
              v-else
              type="checkbox"
              :checked="checkedList.some(v => same(v, option.value))"
              :disabled="disabled"
              @change="toggleInList(option.value, ($event.target as HTMLInputElement).checked)"
            >
            <span>{{ option.label }}</span>
          </label>
        </div>
      </template>
    </fieldset>

    <template v-else>
      <label :class="node.hideLabel ? 'max-sr-only' : 'max-field__label'" :for="id">{{ node.label }}</label>
      <MaxPicker
        v-if="node.input === 'select' || node.input === 'multiselect'"
        :input-id="id"
        :options="node.options"
        :model-value="node.value"
        :multiple="node.input === 'multiselect'"
        :placeholder="node.placeholder"
        :disabled="disabled"
        :options-state="node.optionsState"
        @update:model-value="commit"
      />
      <textarea
        v-else-if="node.input === 'textarea'"
        :id="id"
        class="max-input"
        rows="3"
        :value="node.value ?? ''"
        :placeholder="node.placeholder"
        :disabled="disabled"
        :aria-describedby="node.help ? id + '-help' : undefined"
        @input="write(($event.target as HTMLTextAreaElement).value)"
        @change="commit(($event.target as HTMLTextAreaElement).value)"
      />
      <div v-else-if="node.input === 'stepper'" class="max-stepper">
        <button type="button" class="max-button" data-intent="neutral" data-variant="outline" :disabled="disabled || atMin" :aria-label="ctx.labels.ui.decrease" @click="step(-1)">
          <MaxIcon name="minus" />
        </button>
        <input
          :id="id"
          class="max-input max-stepper__value"
          type="number"
          inputmode="numeric"
          :value="node.value ?? ''"
          :min="node.min"
          :max="node.max"
          :step="node.step"
          :disabled="disabled"
          @change="commit(fieldValue($event.target as HTMLInputElement))"
        >
        <button type="button" class="max-button" data-intent="neutral" data-variant="outline" :disabled="disabled || atMax" :aria-label="ctx.labels.ui.increase" @click="step(1)">
          <MaxIcon name="add" />
        </button>
      </div>
      <div v-else-if="node.input === 'slider'" class="max-slider">
        <input
          :id="id"
          type="range"
          :value="node.value ?? node.min ?? 0"
          :min="node.min ?? 0"
          :max="node.max ?? 100"
          :step="node.step ?? 1"
          :disabled="disabled"
          @input="write(Number(($event.target as HTMLInputElement).value))"
          @change="commit(Number(($event.target as HTMLInputElement).value))"
        >
        <output :for="id" class="max-slider__value">{{ node.value ?? '' }}</output>
      </div>
      <input
        v-else
        :id="id"
        class="max-input"
        :type="node.input === 'number' ? 'number' : node.input === 'search' ? 'search' : node.input === 'date' ? 'date' : node.input === 'datetime' ? 'datetime-local' : node.input === 'time' ? 'time' : 'text'"
        :inputmode="node.input === 'number' ? 'decimal' : undefined"
        :value="node.value ?? ''"
        :placeholder="node.placeholder"
        :min="node.input === 'time' ? undefined : node.min"
        :max="node.input === 'time' ? undefined : node.max"
        :step="node.input === 'time' ? timeStep : node.step"
        :disabled="disabled"
        :aria-describedby="node.help ? id + '-help' : undefined"
        @input="typed(fieldValue($event.target as HTMLInputElement))"
        @change="commit(fieldValue($event.target as HTMLInputElement))"
      >
    </template>
    <p v-if="node.help" :id="id + '-help'" class="max-field__help">{{ node.help }}</p>
  </div>

  <div v-else-if="node.type === 'filterbar'" class="max-control-row max-filterbar" role="search">
    <MaxControl v-for="(filter, i) in node.filters" :key="i" :node="{ ...filter, scope: node.scope }" live />
  </div>

  <div v-else-if="node.type === 'button'" :class="node.block ? 'max-control-block' : 'max-control-inline'">
    <button
      type="button"
      class="max-button"
      :data-intent="node.intent ?? 'neutral'"
      :data-variant="node.variant ?? 'solid'"
      :data-size="node.size ?? 'md'"
      :data-icon-only="node.iconOnly ? 'true' : undefined"
      :disabled="disabled"
      :aria-busy="iface.busy.value || undefined"
      :aria-label="node.iconOnly ? node.label : undefined"
      :title="node.iconOnly ? node.label : undefined"
      @click="iface.run(node.on?.click, scope)"
    >
      <MaxIcon v-if="node.icon" :name="node.icon" />
      <span v-if="!node.iconOnly">{{ node.label }}</span>
    </button>
  </div>

  <div v-else-if="node.type === 'menu'" ref="menuRoot" class="max-menu" @keydown.esc="menuOpen = false">
    <button
      type="button"
      class="max-button"
      data-intent="neutral"
      :data-variant="node.variant ?? 'outline'"
      :data-size="node.size ?? 'md'"
      :data-icon-only="node.iconOnly ? 'true' : undefined"
      aria-haspopup="menu"
      :aria-expanded="menuOpen"
      :aria-label="node.iconOnly ? node.label : undefined"
      :title="node.iconOnly ? node.label : undefined"
      @click="menuOpen = !menuOpen"
    >
      <MaxIcon v-if="node.icon" :name="node.icon" />
      <span v-if="!node.iconOnly">{{ node.label }}</span>
      <MaxIcon v-if="!node.iconOnly" name="chevron-down" size="sm" />
    </button>
    <ul v-if="menuOpen" class="max-menu__list" role="menu">
      <li v-for="item in node.items" :key="item.index" role="none">
        <button
          type="button"
          role="menuitem"
          class="max-menu__item"
          :data-intent="item.intent"
          :disabled="item.disabled || iface.busy.value"
          @click="menuOpen = false; iface.run(item.on?.click, scope)"
        >
          <MaxIcon v-if="item.icon" :name="item.icon" />
          <span>{{ item.label }}</span>
        </button>
      </li>
    </ul>
  </div>

  <div v-else-if="node.type === 'datenav'" class="max-datenav" role="group" :aria-label="node.label">
    <button type="button" class="max-button" data-intent="neutral" data-variant="outline" :aria-label="ctx.labels.ui.previous" :title="ctx.labels.ui.previous" @click="moveDate(-1)">
      <MaxIcon name="chevron-left" />
    </button>
    <input
      :id="id"
      class="max-input max-datenav__input"
      type="date"
      :value="node.value || ''"
      :aria-label="ctx.labels.ui.chooseDate"
      @change="setDate(($event.target as HTMLInputElement).value)"
    >
    <button type="button" class="max-button" data-intent="neutral" data-variant="outline" :aria-label="ctx.labels.ui.next" :title="ctx.labels.ui.next" @click="moveDate(1)">
      <MaxIcon name="chevron-right" />
    </button>
    <button v-if="node.today !== false" type="button" class="max-button" data-intent="neutral" data-variant="outline" :disabled="node.value === todayIso" @click="setDate(todayIso)">
      {{ ctx.labels.ui.today }}
    </button>
    <span v-if="node.format && node.value" class="max-datenav__label" aria-live="polite">{{ dateLabel }}</span>
  </div>

  <figure v-else-if="node.type === 'datatable'" class="max-table-figure" style="display:grid;gap:var(--max-gap-sm)">
    <figcaption v-if="node.title" class="max-panel__title">{{ node.title }}</figcaption>
    <div class="max-table-wrap" tabindex="0" :aria-label="node.title || undefined" :data-narrow="node.narrow">
      <table class="max-table max-datatable">
        <caption v-if="node.caption" class="max-table__caption">{{ node.caption }}</caption>
        <thead>
          <tr>
            <th v-if="selectable" scope="col" class="max-datatable__check">
              <input
                type="checkbox"
                :checked="allSelected"
                :indeterminate.prop="someSelected && !allSelected"
                :disabled="!node.resolvedRows.length || iface.busy.value"
                :aria-label="ctx.labels.ui.selectAll"
                @change="selectAll(($event.target as HTMLInputElement).checked)"
              >
            </th>
            <th
              v-for="col in node.columns"
              :key="col.key"
              scope="col"
              :data-align="align(col)"
              :style="widthStyle(col)"
              :data-sort="node.sort ? '' : undefined"
              @click="sortBy(col)"
            >{{ col.label }}</th>
            <th v-if="node.rowActions?.length" scope="col" data-align="end">
              <span class="max-sr-only">{{ ctx.labels.ui.actions }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!node.resolvedRows.length">
            <td :colspan="columnCount" class="max-datatable__empty">{{ node.empty || ctx.labels.ui.noRows }}</td>
          </tr>
          <tr
            v-for="row in node.resolvedRows"
            :key="row.key"
            :aria-selected="selectable ? isSelected(row.key) : undefined"
            :class="{ 'max-datatable__row--open': !!node.open }"
            :tabindex="node.open ? 0 : undefined"
            @click="openRow($event, row)"
            @keydown.enter.self="openRow($event, row)"
          >
            <td v-if="selectable" class="max-datatable__check">
              <input
                type="checkbox"
                :checked="isSelected(row.key)"
                :disabled="iface.busy.value"
                :aria-label="ctx.labels.ui.selectRow + ' ' + rowLabel(row)"
                @change="selectRow(row.key, ($event.target as HTMLInputElement).checked)"
              >
            </td>
            <td v-for="col in node.columns" :key="col.key" :data-align="align(col)" :data-label="col.label">
              <template v-if="col.edit">
                <input
                  v-if="col.edit.input === 'toggle'"
                  type="checkbox"
                  role="switch"
                  :checked="editValue(col, row) === true"
                  :disabled="iface.busy.value"
                  :aria-label="col.label + ' ' + rowLabel(row)"
                  @change="iface.setIn(col.edit.state, row.key, ($event.target as HTMLInputElement).checked || null)"
                >
                <select
                  v-else-if="col.edit.input === 'select'"
                  class="max-input max-input--cell"
                  :disabled="iface.busy.value"
                  :aria-label="col.label + ' ' + rowLabel(row)"
                  :value="editValue(col, row) ?? ''"
                  @change="iface.setIn(col.edit.state, row.key, ($event.target as HTMLSelectElement).value || null)"
                >
                  <option value="">{{ col.edit.placeholder || '' }}</option>
                  <option v-for="(option, j) in col.edit.options || []" :key="j" :value="option.value">{{ option.label }}</option>
                </select>
                <input
                  v-else
                  class="max-input max-input--cell"
                  :type="col.edit.input === 'number' ? 'number' : col.edit.input === 'date' ? 'date' : col.edit.input === 'time' ? 'time' : 'text'"
                  :inputmode="col.edit.input === 'number' ? 'decimal' : undefined"
                  :value="editValue(col, row) ?? ''"
                  :placeholder="col.edit.placeholder ?? (row.cells[col.key] === null ? '' : String(row.cells[col.key]))"
                  :min="col.edit.input === 'time' ? undefined : col.edit.min"
                  :max="col.edit.input === 'time' ? undefined : col.edit.max"
                  :step="col.edit.input === 'time' ? minutesStep(col.edit.step) : undefined"
                  :disabled="iface.busy.value"
                  :aria-label="col.label + ' ' + rowLabel(row)"
                  @input="iface.setIn(col.edit.state, row.key, cellValue(col, $event.target as HTMLInputElement))"
                >
              </template>
              <template v-else>{{ cell(row.cells[col.key], col) }}</template>
            </td>
            <td v-if="node.rowActions?.length" data-align="end" class="max-datatable__actions">
              <button
                v-for="i in row.actions"
                :key="i"
                type="button"
                class="max-button max-button--small"
                :data-intent="node.rowActions[i].intent ?? 'neutral'"
                :disabled="iface.busy.value"
                @click="iface.run(node.rowActions[i].on?.click, { ...scope, row: row.row })"
              >{{ node.rowActions[i].label }}</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="node.total > node.resolvedRows.length && !node.pageState" class="max-table__more">
        {{ fillLabel(ctx.labels.rowsHidden, { count: node.total - node.resolvedRows.length }) }}
      </div>
      <div v-if="node.pageState" class="max-table__more" style="display:flex;gap:12px;align-items:center">
        <button type="button" class="max-button max-button--small" :disabled="page <= 1 || iface.busy.value" @click="setPage(page - 1)">{{ ctx.labels.ui.previous }}</button>
        <span>{{ page }}</span>
        <button type="button" class="max-button max-button--small" :disabled="page * pageSize >= node.total || iface.busy.value" @click="setPage(page + 1)">{{ ctx.labels.ui.next }}</button>
      </div>
    </div>
  </figure>

  <div v-else-if="node.type === 'calendar'" class="max-control-row">
    <p v-if="!node.resolvedRows?.length" class="max-datatable__empty">{{ node.empty || ctx.labels.ui.noRows }}</p>
    <button
      v-for="row in node.resolvedRows ?? []"
      :key="row.key"
      type="button"
      class="max-button"
      @click="openResolved(row)"
    >{{ row.row.product_name || row.row.name || row.key }}</button>
  </div>

  <aside v-else-if="node.type === 'detail'" class="max-callout">
    <p v-if="node.title" class="max-callout__title">{{ node.title }}</p>
    <p v-if="!node.resolvedRow">{{ node.empty || ctx.labels.ui.noRows }}</p>
    <dl v-else>
      <template v-for="field in node.fields" :key="field.key">
        <dt>{{ field.label }}</dt>
        <dd>{{ cell(node.resolvedRow[field.key], field) }}</dd>
      </template>
    </dl>
    <button v-if="node.resolvedRow && node.open" type="button" class="max-button" @click="openResolved({ key: String(node.resolvedRow.id ?? ''), cells: node.resolvedRow, row: node.resolvedRow, actions: [] })">{{ node.open.to }}</button>
  </aside>

  <div v-else-if="node.type === 'board'" class="max-control-row" style="display:flex;gap:16px;align-items:flex-start">
    <div v-for="(column, index) in node.columns ?? []" :key="index">
      <strong>{{ column.title }}</strong>
      <button
        v-for="row in column.resolvedRows ?? []"
        :key="row.key"
        type="button"
        class="max-button"
        @click="openResolved(row)"
      >{{ row.row.name || row.key }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useId } from 'vue'
import MaxPicker from './MaxPicker.vue'
import MaxIcon from './MaxIcon.vue'
import { formatValue } from '../core/format.ts'
import { evalExpr } from '../core/expr.ts'
import { fillLabel } from '../core/labels.ts'
import { normalizeTime } from '../core/interface.ts'
import type { ResolvedRow } from '../core/interface-runtime.ts'
import { useMaxContext, useMaxInterface, type MaxInterfaceContext } from '../runtime/context.ts'

/** live: a filterbar search, which commits while the user types */
const props = defineProps<{ node: any, live?: boolean }>()
const ctx = useMaxContext()
const iface = useMaxInterface() as MaxInterfaceContext
const id = 'max-f-' + useId()
const LIVE_DELAY_MS = 300

/** The expression scope the node was resolved in (repeat item, loop, props), for its handlers */
const scope = computed<Record<string, unknown>>(() => props.node.scope ?? {})

// Fields stay enabled while busy: disabling a focused input drops focus mid-typing
const disabled = computed(() => props.node.disabled === true || (props.node.type === 'button' && iface.busy.value))

let pending: ReturnType<typeof setTimeout> | null = null
let committed: unknown

/** Writes the field's state, or one entry of a map state when the field has a key */
function write (value: unknown) {
  if (props.node.stateKey !== undefined) iface.setIn(props.node.state, props.node.stateKey, value)
  else iface.setState(props.node.state, value)
}

function commit (value: unknown) {
  if (pending) clearTimeout(pending)
  pending = null
  write(value)
  if (props.live && value === committed) return
  committed = value
  const change = props.node.on?.change
  if (change) iface.run(change, { ...scope.value, event: { value } })
}

function typed (value: unknown) {
  write(value)
  if (!props.live) return
  if (pending) clearTimeout(pending)
  pending = setTimeout(() => commit(value), LIVE_DELAY_MS)
}

/** A time input's step in seconds, as the input expects; the spec gives minutes */
const minutesStep = (step: unknown) => typeof step === 'number' && step >= 1 ? Math.round(step) * 60 : 60
const timeStep = computed(() => minutesStep(props.node.step))

function fieldValue (el: HTMLInputElement): unknown {
  if (props.node.input === 'time') return normalizeTime(el.value)
  if (props.node.input !== 'number' && props.node.input !== 'stepper') return el.value
  return el.value === '' ? null : Number(el.value)
}

// Options --------------------------------------------------------------------

type Option = { label: string, value: string | number }
const isChoice = computed(() => ['chips', 'segmented', 'radio', 'checkboxes'].includes(props.node.input))
const optionGroups = computed<{ label: string, options: Option[] }[]>(() => {
  const groups: { label: string, options: Option[] }[] = []
  for (const o of (props.node.options ?? []) as any[]) {
    if (Array.isArray(o?.options)) groups.push({ label: o.label, options: o.options })
    else {
      const last = groups[groups.length - 1]
      if (last && !last.label) last.options.push(o)
      else groups.push({ label: '', options: [o] })
    }
  }
  return groups
})
const flatOptions = computed(() => optionGroups.value.flatMap(g => g.options))
const same = (a: unknown, b: unknown) => a !== null && a !== undefined && String(a) === String(b)
const hasValue = computed(() => flatOptions.value.some(o => same(o.value, props.node.value)))
const checkedList = computed<unknown[]>(() => Array.isArray(props.node.value) ? props.node.value : [])

function toggleInList (value: string | number, on: boolean) {
  const rest = checkedList.value.filter(v => !same(v, value))
  commit(on ? [...rest, value] : rest)
}

function arrowKeys (event: KeyboardEvent, i: number) {
  const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
  if (!delta) return
  event.preventDefault()
  const options = flatOptions.value
  const next = options[(i + delta + options.length) % options.length]
  if (!next) return
  commit(next.value)
  const group = (event.currentTarget as HTMLElement).parentElement
  const buttons = group ? Array.from(group.querySelectorAll('button')) : []
  buttons[(i + delta + options.length) % options.length]?.focus()
}

// Stepper --------------------------------------------------------------------

const atMin = computed(() => typeof props.node.min === 'number' && typeof props.node.value === 'number' && props.node.value <= props.node.min)
const atMax = computed(() => typeof props.node.max === 'number' && typeof props.node.value === 'number' && props.node.value >= props.node.max)
function step (direction: number) {
  const size = typeof props.node.step === 'number' && props.node.step > 0 ? props.node.step : 1
  const current = typeof props.node.value === 'number' ? props.node.value : (typeof props.node.min === 'number' ? props.node.min : 0)
  let next = current + direction * size
  if (typeof props.node.min === 'number') next = Math.max(props.node.min, next)
  if (typeof props.node.max === 'number') next = Math.min(props.node.max, next)
  commit(next)
}

// Date navigation ------------------------------------------------------------

const env = computed(() => ({ locale: ctx.locale }))
const todayIso = computed(() => String(evalExpr({ today: [] }, {}, env.value) ?? ''))
function setDate (iso: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return
  commit(iso)
}
function moveDate (direction: number) {
  const base = /^\d{4}-\d{2}-\d{2}$/.test(String(props.node.value)) ? String(props.node.value) : todayIso.value
  const unit = props.node.step ?? 'day'
  if (unit === 'month') {
    const [y, m, d] = base.split('-').map(Number) as [number, number, number]
    const target = new Date(Date.UTC(y, m - 1 + direction, 1))
    const days = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
    target.setUTCDate(Math.min(d, days))
    setDate(target.toISOString().slice(0, 10))
    return
  }
  setDate(String(evalExpr({ addDays: [base, direction * (unit === 'week' ? 7 : 1)] }, {}, env.value)))
}
const dateLabel = computed(() => String(evalExpr({ fmt: [props.node.value, props.node.format] }, {}, env.value) ?? ''))

// Menu -----------------------------------------------------------------------

const menuOpen = ref(false)
const menuRoot = ref<HTMLElement | null>(null)
function outside (event: Event) {
  if (menuOpen.value && menuRoot.value && !menuRoot.value.contains(event.target as Node)) menuOpen.value = false
}
onMounted(() => { if (props.node.type === 'menu') document.addEventListener('click', outside) })
onBeforeUnmount(() => {
  if (pending) clearTimeout(pending)
  document.removeEventListener('click', outside)
})

// Tables ---------------------------------------------------------------------

const NUMERIC = ['number', 'integer', 'decimal', 'currency', 'percent', 'duration']
const align = (col: any) => col.align ?? (col.edit?.input === 'number' || (col.format && NUMERIC.includes(col.format)) ? 'end' : 'start')
const widthStyle = (col: any) => col.width === 'narrow' ? { width: '1%' } : col.width === 'wide' ? { width: '40%' } : undefined
const cell = (value: unknown, col: any) => {
  if (typeof value === 'boolean') return value ? '✓' : '–'
  return formatValue(value, { format: col.format, currency: col.currency, locale: ctx.locale, labels: ctx.labels.catalog })
}

const columnCount = computed(() => props.node.columns.length + (selectable.value ? 1 : 0) + (props.node.rowActions?.length ? 1 : 0))
const firstColumn = computed(() => props.node.columns.find((c: any) => !c.edit)?.key)
const rowLabel = (row: ResolvedRow) => {
  const v = firstColumn.value ? row.cells[firstColumn.value] : null
  return v === null || v === undefined ? row.key : String(v)
}

const editValue = (col: any, row: ResolvedRow) => props.node.edits?.[col.key]?.[row.key]
function cellValue (col: any, el: HTMLInputElement): unknown {
  if (col.edit.input === 'time') return normalizeTime(el.value)
  if (col.edit.input !== 'number') return el.value
  if (el.value === '') return null
  const n = Number(el.value)
  return Number.isFinite(n) ? n : null
}

const INTERACTIVE = 'input, button, select, textarea, label, a, .max-datatable__check'

function openRow (event: Event, row: ResolvedRow) {
  const open = props.node.open
  if (!open || (event.target as HTMLElement | null)?.closest?.(INTERACTIVE)) return
  iface.run([{ do: 'navigate', to: open.to, id: open.id ?? { var: 'row.' + props.node.rowKey } }], { ...scope.value, row: row.row })
}

const selectable = computed(() => Array.isArray(props.node.selected))
const selected = computed(() => new Set<string>(props.node.selected ?? []))
const isSelected = (key: string) => selected.value.has(key)
const allSelected = computed(() => props.node.resolvedRows.length > 0 && props.node.resolvedRows.every((r: ResolvedRow) => selected.value.has(r.key)))
const someSelected = computed(() => props.node.resolvedRows.some((r: ResolvedRow) => selected.value.has(r.key)))

function applySelection (keys: string[]) {
  iface.setSelection(props.node.select, keys, props.node.resolvedRows, props.node.rowKey)
}

function selectRow (key: string, on: boolean) {
  const visible = props.node.resolvedRows.map((r: ResolvedRow) => r.key).filter((k: string) => k === key ? on : selected.value.has(k))
  applySelection(visible)
}

function selectAll (on: boolean) {
  applySelection(on ? props.node.resolvedRows.map((r: ResolvedRow) => r.key) : [])
}

const page = computed(() => {
  const n = Number(props.node.page)
  return Number.isFinite(n) && n > 0 ? n : 1
})
const pageSize = computed(() => typeof props.node.pageSize === 'number' ? props.node.pageSize : 50)

function sortBy (col: { key: string }) {
  if (typeof props.node.sort !== 'string') return
  const nextDir = props.node.sortValue === col.key && props.node.sortDirValue !== 'asc' ? 'asc' : 'desc'
  iface.setState(props.node.sort, col.key)
  if (typeof props.node.sortDir === 'string') iface.setState(props.node.sortDir, nextDir)
  if (props.node.on?.sort) iface.run(props.node.on.sort, scope.value)
}

function setPage (next: number) {
  iface.setState(props.node.pageState, next)
  if (props.node.on?.page) iface.run(props.node.on.page, scope.value)
}

function openResolved (row: ResolvedRow) {
  const open = props.node.open
  if (!open) return
  iface.run([{ do: 'navigate', to: open.to, id: open.id ?? { var: 'row.id' } }], { ...scope.value, row: row.row })
}
</script>
